import { RouteObject } from "react-router";
import { InfoRoutes } from "src/router/routes/jobs/Info";
import { DocumentsRoutes } from "src/router/routes/jobs/Documents";
import { FinancingRoutes } from "src/router/routes/jobs/Financing";
import { MeasurementsRoutes } from "src/router/routes/jobs/Measurements";
import { EstimatesRoutes } from "src/router/routes/jobs/Estimates";
import { InspectionRoutes } from "src/router/routes/jobs/Inspection";
import { NotesRoutes } from "src/router/routes/jobs/Notes";
import { ContractsRoutes } from "src/router/routes/jobs/Contracts";
import { EventRoutes } from "src/router/routes/jobs/EventRoutes";
import { SignatureRoutes } from "src/router/routes/jobs/Signatures";
import { setupMakePaymentRoutes } from "src/router/routes/common/MakePayment";
import { Redirect } from "src/router/helper/Redirect";
import { PresentationsRoutes } from "src/router/routes/jobs/Presentations";

export const reloadJobValues = ["info"];

// '/jobs/*'
export const JobRoutes: RouteObject[] = [
  {
    path: ":jobId/screen_shares/:roomUid",
    async lazy() {
      const { Host } = await import("customer/ScreenShare/Host");
      return { element: <Host /> };
    },
  },
  {
    path: ":jobId",
    async lazy() {
      const { Job } = await import("jobs/Job");
      return { element: <Job /> };
    },
    shouldRevalidate: ({ currentUrl, nextUrl }) => {
      return reloadJobValues.some((value) => nextUrl.pathname.includes(value) && !currentUrl.pathname.includes(value));
    },
    async loader(args) {
      const { jobLoader } = await import("src/router/loaders/Jobs");
      return jobLoader(args);
    },
    children: [
      {
        path: "",
        children: [
          {
            path: "info",
            children: InfoRoutes,
          },
          {
            path: "financing",
            async lazy() {
              const { FinancingTab } = await import("jobs/Job/FinancingTab");
              return { element: <FinancingTab /> };
            },
            async loader(args) {
              const { financingTabLoader } = await import("src/router/loaders/Jobs");
              return financingTabLoader(args);
            },
            children: FinancingRoutes,
          },
          {
            path: "appointments",
            children: EventRoutes,
            async lazy() {
              const { AppointmentsTab } = await import("jobs/Job/AppointmentsTab");
              return { element: <AppointmentsTab /> };
            },
          },
          {
            path: "tools",
            async loader(args) {
              const { toolsTabLoader } = await import("src/router/loaders/Jobs");
              return toolsTabLoader(args);
            },
            async lazy() {
              const { ToolsTab } = await import("jobs/Job/ToolsTab");
              return { element: <ToolsTab /> };
            },
          },
          {
            path: "images",
            async loader(args) {
              const { imagesTabLoader } = await import("src/router/loaders/Jobs");
              return imagesTabLoader(args);
            },
            async lazy() {
              const { ImagesTab } = await import("jobs/Job/ImagesTab");
              return { element: <ImagesTab /> };
            },
            children: [
              {
                path: ":imageId",
                async lazy() {
                  const { EditImageModal } = await import("jobs/Job/ImagesTab/EditImageModal");
                  return { element: <EditImageModal /> };
                },
              },
              {
                path: ":imageId/carousel",
                async lazy() {
                  const { ImageCarousel } = await import("jobs/Job/ImagesTab/ImageCarousel");
                  return { element: <ImageCarousel /> };
                },
              },
            ],
          },
          {
            path: "documents",
            children: DocumentsRoutes,
          },
          {
            path: "measurements",
            children: MeasurementsRoutes,
          },
          {
            path: "inspection",
            async lazy() {
              const { InspectionsTab } = await import("jobs/Job/InspectionsTab");
              return { element: <InspectionsTab /> };
            },
            children: InspectionRoutes,
          },
          {
            path: "history",
            async lazy() {
              const { HistoryTab } = await import("jobs/Job/HistoryTab");
              return { element: <HistoryTab /> };
            },
            children: [
              {
                path: "email_history",
                async loader(args) {
                  const { emailsLoader } = await import("src/router/loaders/jobs/History");
                  return emailsLoader(args);
                },
                async lazy() {
                  const { EmailHistory } = await import("jobs/Job/HistoryTab/EmailHistory");
                  return { element: <EmailHistory /> };
                },
              },
              {
                path: "analytics_report",
                async loader(args) {
                  const { analyticsReportLoader } = await import("src/router/loaders/jobs/History");

                  return analyticsReportLoader(args);
                },
                async lazy() {
                  const { AnalyticsReport } = await import("jobs/Job/HistoryTab/AnalyticsReport");
                  return { element: <AnalyticsReport /> };
                },
              },
              {
                path: "analytics_history",
                async loader(args) {
                  const { analyticsHistoryLoader } = await import("src/router/loaders/jobs/History");

                  return analyticsHistoryLoader(args);
                },
                async lazy() {
                  const { AnalyticsHistory } = await import("jobs/Job/HistoryTab/AnalyticsHistory");
                  return { element: <AnalyticsHistory /> };
                },
              },
              {
                path: "status_history",
                async lazy() {
                  const { StatusHistory } = await import("jobs/Job/HistoryTab/StatusHistory");
                  return { element: <StatusHistory /> };
                },
                async loader(args) {
                  const { jobStateLogsLoader } = await import("src/router/loaders/jobs/History");
                  return jobStateLogsLoader(args);
                },
              },
              {
                path: "",
                element: <Redirect to="email_history" />,
              },
            ],
          },
          {
            path: "notes",
            children: NotesRoutes,
          },
          {
            path: "presentations",
            children: PresentationsRoutes,
          },
          {
            path: "design-tools",
            async lazy() {
              const { DesignToolsTab } = await import("jobs/Job/DesignToolsTab");
              return { element: <DesignToolsTab /> };
            },
            async loader(args) {
              const { designToolsTabLoader } = await import("src/router/loaders/Jobs");
              return designToolsTabLoader(args);
            },
            children: [
              {
                path: "",
                async lazy() {
                  const { DesignToolsPanel } = await import("jobs/Job/DesignToolsTab/DesignToolsPanel");
                  return { element: <DesignToolsPanel /> };
                },
              },
              {
                path: "visualizations",
                async lazy() {
                  const { VisualizationPanel } = await import("jobs/Job/DesignToolsTab/VisualizationPanel");
                  return { element: <VisualizationPanel /> };
                },
                async loader(args) {
                  const { visualizationTabLoader } = await import("src/router/loaders/Jobs");
                  return visualizationTabLoader(args);
                },
                children: [
                  {
                    path: ":visualizationId/edit",
                    async lazy() {
                      const { EditModal } = await import("jobs/Job/DesignToolsTab/VisualizationPanel/EditModal");
                      return { element: <EditModal /> };
                    },
                  },
                ],
              },
              {
                path: ":toolKey",
                async lazy() {
                  const { ToolIframe } = await import("jobs/Job/DesignToolsTab/DesignToolsPanel/ToolIframe");
                  return { element: <ToolIframe /> };
                },
              },
              {
                path: "visualizations/:toolKey",
                async lazy() {
                  const { VisualizationIframe } =
                    await import("jobs/Job/DesignToolsTab/VisualizationPanel/VisualizationIframe");
                  return { element: <VisualizationIframe /> };
                },
              },
            ],
          },
          {
            path: "estimates",
            async loader(args) {
              const { estimatesTabLoader } = await import("src/router/loaders/Jobs");
              return estimatesTabLoader(args);
            },
            shouldRevalidate({ currentUrl, nextUrl, defaultShouldRevalidate }) {
              // Don't revalidate if only search params changed (e.g., editingLineItemId)
              if (currentUrl.pathname === nextUrl.pathname) {
                return false;
              }
              return defaultShouldRevalidate;
            },
            children: EstimatesRoutes,
          },
          {
            path: "proposals",
            async loader(args) {
              const { proposalsTabLoader } = await import("src/router/loaders/Jobs");
              return proposalsTabLoader(args);
            },
            children: [
              {
                path: ":estimateId",
                shouldRevalidate: ({ currentParams, nextParams }) => currentParams.estimateId !== nextParams.estimateId,
                async loader(args) {
                  const { proposalLoader } = await import("src/router/loaders/Jobs");
                  return proposalLoader(args);
                },
                async lazy() {
                  const { ViewProposal } = await import("jobs/Job/ProposalsTab/ViewProposal");
                  return { element: <ViewProposal /> };
                },
                children: [
                  {
                    path: "email",
                    async lazy() {
                      const { EmailEstimate } = await import("jobs/components/EmailEstimate");
                      return { element: <EmailEstimate /> };
                    },
                  },
                  ...setupMakePaymentRoutes(),
                ],
              },
              {
                path: "",
                async loader(args) {
                  const { proposalsLoader } = await import("src/router/loaders/jobs/Estimates");
                  return proposalsLoader(args);
                },
                async lazy() {
                  const { ProposalsTab } = await import("jobs/Job/ProposalsTab");
                  return { element: <ProposalsTab /> };
                },
              },
              ...setupMakePaymentRoutes(),
            ],
          },
          {
            path: "contracts",
            async loader(args) {
              const { contractsTabLoader } = await import("src/router/loaders/Jobs");
              return contractsTabLoader(args);
            },
            async lazy() {
              const { ContractsTracking } = await import("jobs/Job/ContractsTab/ContractsTracking");
              return { element: <ContractsTracking /> };
            },
            children: ContractsRoutes,
          },
          {
            path: "signatures",
            async lazy() {
              const { SignaturesTab } = await import("jobs/Job/SignaturesTab");
              return { element: <SignaturesTab /> };
            },
            children: SignatureRoutes,
          },
          {
            path: "",
            element: <Redirect to="info" />,
          },
          {
            path: "*",
            element: <Redirect to="info" />,
          },
        ],
      },
    ],
  },
  {
    path: "",
    async loader(args) {
      const { jobsLoader } = await import("src/router/loaders/Jobs");
      return jobsLoader(args);
    },
    async lazy() {
      const { List } = await import("jobs/List");
      return { element: <List /> };
    },
    children: [
      {
        path: "filters",
        async loader(args) {
          const { filtersLoader } = await import("src/router/loaders/Jobs");
          return filtersLoader(args);
        },
        async lazy() {
          const { Filters } = await import("jobs/List/Filters");
          return { element: <Filters jobParamsType="jobParams" /> };
        },
      },
      {
        path: "new",
        async loader(args) {
          const { newJobLoader } = await import("src/router/loaders/Jobs");
          return newJobLoader(args);
        },
        async lazy() {
          const { AddJobModal } = await import("jobs/List/AddJobModal");
          return { element: <AddJobModal /> };
        },
      },
    ],
  },
];
